import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Skeleton, Typography } from '@mui/material';
import { useAccessFeatures } from '../../../auth/useAccessFeatures';
import type { ExportMarket } from '../event/export/exportTypes';
import type { SearchScope, SortDir } from './api/searchTypes';
import { useSearchSchema } from './api/useSearchSchema';
import { useMarketSearch } from './api/useMarketSearch';
import {
    DEFAULTS,
    activeFilterCount,
    buildApiQuery,
    defaultsFor,
    dirOf,
    exclusiveToggle,
    listState,
    readList,
    readParam,
    resetFilters,
    scopeOf,
    toggleInList,
    withParam,
} from './state/searchParams';
import { switchScope, type ScopeMemory } from './state/scopeMemory';
import { SearchBar } from './components/SearchBar';
import { ScopeChips } from './components/ScopeChips';
import { FilterPanel } from './components/FilterPanel';
import { ResultsHeader } from './components/ResultsHeader';
import { PaginationBar } from './components/PaginationBar';
import { EventRow } from './components/EventRow';
import { TraderResults } from './components/TraderResults';
import { minTableWidth } from './components/resultColumns';
import { SearchExportButton } from './components/SearchExportButton';
import { exportMarketsOf } from './components/searchExport';

const DEFAULT_PAGE_SIZE = 50;

export function SearchView() {
    const [params, setParams] = useSearchParams();
    const {
        data: schema,
        loading: schemaLoading,
        error: schemaError,
    } = useSearchSchema();
    const [selected, setSelected] = useState<Set<string>>(() => new Set());
    const seenMarkets = useRef(new Map<string, ExportMarket>());
    // The other scope group's parameters, for this session (spec 6.2).
    const scopeMemory = useRef<ScopeMemory>({});
    const isAdmin = useAccessFeatures().role === 'admin';

    const urlScope = scopeOf(params);
    const traderReady = Boolean(schema?.scopes.includes('trader'));
    // irm may deploy before the indexer serves traders (spec 8): until the
    // schema lists the scope, a trader link shows events.
    const scope: SearchScope =
        urlScope === 'trader' && !traderReady ? 'event' : urlScope;
    const spec = useMemo(
        () => schema?.filter_spec[scope] ?? [],
        [schema, scope]
    );
    const defaults = useMemo(
        () => (schema ? defaultsFor(scope, schema) : DEFAULTS),
        [schema, scope]
    );
    const pageSize = Number(params.get('limit')) || DEFAULT_PAGE_SIZE;

    const query = useMemo(() => {
        if (!schema || scope === 'trader' || scope !== urlScope) return null;
        return buildApiQuery(params, spec, pageSize, null);
    }, [schema, scope, urlScope, params, spec, pageSize]);

    const {
        events,
        loading,
        error,
        total,
        pageIndex,
        hasPrev,
        hasNext,
        nextPage,
        prevPage,
    } = useMarketSearch(query);

    const update = useCallback(
        (next: URLSearchParams) => setParams(next, { replace: true }),
        [setParams]
    );

    useEffect(() => {
        if (schema && urlScope === 'trader' && !traderReady) {
            update(withParam(params, 'scope', null));
        }
    }, [schema, urlScope, traderReady, params, update]);

    const changeScope = useCallback(
        (nextScope: SearchScope) => {
            if (!schema || nextScope === scope) return;
            const result = switchScope(
                params,
                scope,
                nextScope,
                scopeMemory.current,
                schema.sort_options[nextScope] ?? []
            );
            scopeMemory.current = result.memory;
            update(result.params);
        },
        [schema, scope, params, update]
    );

    const setValue = useCallback(
        (key: string, value: string | null) =>
            update(withParam(params, key, value)),
        [params, update]
    );

    const toggleExclusive = useCallback(
        (
            includeKey: string,
            excludeKey: string,
            value: string,
            target: 'include' | 'exclude'
        ) =>
            update(
                exclusiveToggle(params, includeKey, excludeKey, value, target)
            ),
        [params, update]
    );

    const seriesSelected = readList(params, 'series');

    const tagState = useCallback(
        (tag: string) => listState(params, 'tags_include', 'tags_exclude', tag),
        [params]
    );

    const categoryState = useCallback(
        (category: string) =>
            listState(params, 'event_include', 'event_exclude', category),
        [params]
    );

    // A series spans many events that are already closed, so the default
    // status=open filter would hide all of them but the clicked one.
    const selectSeries = useCallback(
        (slug: string) => {
            const turningOn = !readList(params, 'series').includes(slug);
            let next = toggleInList(params, 'series', slug);
            if (turningOn) next = withParam(next, 'status', 'all');
            update(next);
        },
        [params, update]
    );

    const sort = readParam(params, 'sort');
    const dir = dirOf(params, 'dir');
    const statusFilter = readParam(params, 'status');
    const hasQuery = Boolean(params.get('q'));
    const filterCount = schema ? activeFilterCount(params, spec) : 0;

    const sortableKeys = useMemo(() => {
        if (!schema) return [];
        return (schema.sort_options[scope] ?? []).filter(
            k => k !== 'relevance' || hasQuery
        );
    }, [schema, scope, hasQuery]);

    const onSort = useCallback(
        (key: string) => {
            let next = withParam(params, 'sort', key);
            next =
                key !== sort
                    ? withParam(next, 'dir', 'desc')
                    : withParam(next, 'dir', dir === 'desc' ? 'asc' : 'desc');
            update(next);
        },
        [params, sort, dir, update]
    );

    const pageMarketIds = useMemo(
        () => events.flatMap(e => e.markets.map(m => m.market_id)),
        [events]
    );

    useEffect(() => {
        for (const event of events) {
            for (const market of exportMarketsOf(event)) {
                seenMarkets.current.set(market.conditionId, market);
            }
        }
    }, [events]);

    const selectedMarkets = useMemo(
        () =>
            [...selected].flatMap(id => {
                const market = seenMarkets.current.get(id);
                return market ? [market] : [];
            }),
        [selected]
    );

    const pageSelectedCount = useMemo(
        () => pageMarketIds.filter(id => selected.has(id)).length,
        [pageMarketIds, selected]
    );

    const pageAllSelected =
        pageMarketIds.length > 0 && pageSelectedCount === pageMarketIds.length;
    const pageSomeSelected = pageSelectedCount > 0 && !pageAllSelected;

    const toggleMarket = useCallback((marketId: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(marketId)) next.delete(marketId);
            else next.add(marketId);
            return next;
        });
    }, []);

    const setMarkets = useCallback((marketIds: string[], value: boolean) => {
        setSelected(prev => {
            const next = new Set(prev);
            for (const id of marketIds) {
                if (value) next.add(id);
                else next.delete(id);
            }
            return next;
        });
    }, []);

    const togglePageSelection = useCallback(
        (value: boolean) => setMarkets(pageMarketIds, value),
        [pageMarketIds, setMarkets]
    );

    const clearSelection = useCallback(() => setSelected(new Set()), []);

    const minWidth = useMemo(() => minTableWidth(), []);

    if (schemaError) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    Could not load the search options. Reload the page to try
                    again.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                p: 0.5,
                gap: 1,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 250, flexShrink: 0 }}>
                    <ScopeChips
                        scope={scope}
                        scopes={schema?.scopes ?? ['event', 'market']}
                        disabled={!schema}
                        onChange={changeScope}
                    />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SearchBar
                        value={params.get('q') ?? ''}
                        scope={scope}
                        onQueryChange={value => {
                            let next = withParam(params, 'q', value || null);
                            if (
                                !value &&
                                readParam(next, 'sort') === 'relevance'
                            ) {
                                next = withParam(next, 'sort', null);
                            }
                            update(next);
                        }}
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minHeight: 0 }}>
                <Box
                    sx={{
                        width: 250,
                        flexShrink: 0,
                        minHeight: 0,
                        display: 'flex',
                    }}
                >
                    {schemaLoading || !schema ? (
                        <Skeleton
                            variant="rectangular"
                            height={400}
                            sx={{ width: '100%' }}
                        />
                    ) : (
                        <FilterPanel
                            schema={schema}
                            spec={spec}
                            params={params}
                            scope={scope}
                            defaults={defaults}
                            onChange={update}
                            onReset={() => update(resetFilters(params, spec))}
                        />
                    )}
                </Box>

                {scope === 'trader' && schema ? (
                    <TraderResults
                        schema={schema}
                        spec={spec}
                        params={params}
                        defaults={defaults}
                        onChange={update}
                    />
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: '4px',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                            <Box sx={{ minWidth }}>
                                {schema && (
                                    <ResultsHeader
                                        sort={sort}
                                        dir={dir}
                                        sortableKeys={sortableKeys}
                                        sortInfo={schema.sort_info}
                                        marketSort={readParam(
                                            params,
                                            'market_sort'
                                        )}
                                        marketDir={dirOf(params, 'market_dir')}
                                        marketSortOptions={
                                            schema.market_sort_options
                                        }
                                        marketSortInfo={schema.market_sort_info}
                                        pageAllSelected={pageAllSelected}
                                        pageSomeSelected={pageSomeSelected}
                                        onSort={onSort}
                                        onMarketSortChange={value =>
                                            setValue('market_sort', value)
                                        }
                                        onMarketDirChange={(next: SortDir) =>
                                            setValue('market_dir', next)
                                        }
                                        onTogglePageSelection={
                                            togglePageSelection
                                        }
                                    />
                                )}

                                {error && (
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: 'text.disabled',
                                            p: 1,
                                        }}
                                    >
                                        {error}
                                    </Typography>
                                )}

                                {loading &&
                                    [...Array(10)].map((_, i) => (
                                        <Skeleton
                                            key={i}
                                            variant="rectangular"
                                            height={44}
                                            sx={{ mx: 1.25, my: 0.4 }}
                                        />
                                    ))}

                                {!loading && events.length === 0 && !error && (
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: 'text.disabled',
                                            p: 1.5,
                                        }}
                                    >
                                        {filterCount > 0 || hasQuery
                                            ? 'Nothing matches. Loosen a filter or drop a word from the query.'
                                            : 'No open events found.'}
                                    </Typography>
                                )}

                                {!loading &&
                                    events.map(event => (
                                        <EventRow
                                            key={event.event_id}
                                            event={event}
                                            scope={scope}
                                            statusFilter={statusFilter}
                                            primaryTags={
                                                schema?.polymarket_tags ?? []
                                            }
                                            tagState={tagState}
                                            categoryState={categoryState}
                                            seriesActive={
                                                event.series
                                                    ? seriesSelected.includes(
                                                          event.series.slug
                                                      )
                                                    : false
                                            }
                                            selected={selected}
                                            showRaw={isAdmin}
                                            onIncludeTag={tag =>
                                                toggleExclusive(
                                                    'tags_include',
                                                    'tags_exclude',
                                                    tag,
                                                    'include'
                                                )
                                            }
                                            onExcludeTag={tag =>
                                                toggleExclusive(
                                                    'tags_include',
                                                    'tags_exclude',
                                                    tag,
                                                    'exclude'
                                                )
                                            }
                                            onIncludeCategory={category =>
                                                toggleExclusive(
                                                    'event_include',
                                                    'event_exclude',
                                                    category,
                                                    'include'
                                                )
                                            }
                                            onExcludeCategory={category =>
                                                toggleExclusive(
                                                    'event_include',
                                                    'event_exclude',
                                                    category,
                                                    'exclude'
                                                )
                                            }
                                            onSelectSeries={selectSeries}
                                            onToggleMarket={toggleMarket}
                                            onSetMarkets={setMarkets}
                                        />
                                    ))}
                            </Box>
                        </Box>
                        <PaginationBar
                            pageIndex={pageIndex}
                            pageSize={pageSize}
                            pageCount={events.length}
                            total={total}
                            hasPrev={hasPrev}
                            hasNext={hasNext}
                            busy={loading}
                            selectedCount={selected.size}
                            selectionActions={
                                <SearchExportButton
                                    markets={selectedMarkets}
                                    label={`Download (${selectedMarkets.length.toLocaleString()})`}
                                    tooltip="Download the data of the selected markets"
                                />
                            }
                            onPrev={prevPage}
                            onNext={nextPage}
                            onPageSizeChange={size =>
                                setValue(
                                    'limit',
                                    size === DEFAULT_PAGE_SIZE
                                        ? null
                                        : String(size)
                                )
                            }
                            onClearSelection={clearSelection}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default SearchView;
